import { coarsenMeta } from "./privacy-location";
import { analyticsStorage } from "./consent-region";
/**
 * Client-side event tracking.
 * Logs events to /api/track which writes to Supabase.
 * Fires and forgets — never blocks the UI or throws errors to the user.
 *
 * Each browser gets a persistent anonymous device ID (stored in localStorage)
 * so the admin can exclude specific devices from analytics.
 */

type TrackEvent = {
  event: string;         // e.g. "trial_click", "search", "signup", "page_view"
  nctId?: string;        // ClinicalTrials.gov ID
  disease?: string;      // disease name or slug
  trialTitle?: string;   // brief title of the trial
  sponsor?: string;      // pharma/biotech sponsor name
  source?: string;       // which page/component triggered this
  meta?: Record<string, string | number | boolean>;  // any extra data
};

/** Get or create a stable anonymous device ID for this browser. */
function getDeviceId(): string {
  const KEY = "tf_device_id";
  try {
    // Saved in the browser only where allowed (lib/consent-region.ts); for
    // visitors in consent countries who have not accepted, it lives in memory
    // for this visit only.
    let id = analyticsStorage.get("local", KEY);
    if (id) return id;
    // Crypto.randomUUID is available in all modern browsers
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "d-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    analyticsStorage.set("local", KEY, id);
    return id;
  } catch {
    // Private browsing or storage blocked — return a transient ID
    return "tmp-" + Math.random().toString(36).slice(2);
  }
}

/**
 * Owner browsers. Any browser that has signed in to /admin is marked as the
 * site owner's, and from then on it sends nothing to the site's own tracker.
 * (GA4 was removed on October 12, 2026; the site has no ad trackers.)
 */
const OWNER_KEY = "tf_owner";
// Owner link (October 11, 2026). Opening any page with ?tf_me=jy1p90parl marks the
// browser as Jason's before anything is tracked, then removes the tag from
// the address bar. It works in private tabs, where the saved flag and device
// ID vanish when the tab closes, because the bookmark carries it every time.
// It only turns tracking off for that browser and grants no access.
const OWNER_PARAM = "tf_me";
const OWNER_CODE = "jy1p90parl";
function claimOwnerFromUrl(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const u = new URL(window.location.href);
    if (u.searchParams.get(OWNER_PARAM) !== OWNER_CODE) return false;
    try { localStorage.setItem(OWNER_KEY, "1"); } catch { /* storage blocked */ }
    try { sessionStorage.setItem(OWNER_KEY, "1"); } catch { /* storage blocked */ }
    u.searchParams.delete(OWNER_PARAM);
    window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
    return true;
  } catch { return false; }
}
export function isOwnerBrowser(): boolean {
  if (claimOwnerFromUrl()) return true;
  try { if (typeof localStorage !== "undefined" && localStorage.getItem(OWNER_KEY) === "1") return true; } catch { /* storage blocked */ }
  try { return typeof sessionStorage !== "undefined" && sessionStorage.getItem(OWNER_KEY) === "1"; } catch { return false; }
}
export function markOwnerBrowser(on = true): void {
  try { if (on) localStorage.setItem(OWNER_KEY, "1"); else localStorage.removeItem(OWNER_KEY); } catch { /* storage blocked */ }
}

/**
 * Client-side bot signals. If any of these are true, skip tracking.
 * We prefer to be a little aggressive here — a missed real user is far
 * better than polluting analytics with automation traffic.
 */
function looksLikeBot(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;

  // navigator.webdriver is set to true by Selenium/Puppeteer/Playwright by default.
  // Stealth patches can hide it, but most scrapers don't bother.
  if ((navigator as { webdriver?: boolean }).webdriver === true) return true;

  const ua = navigator.userAgent || "";
  const low = ua.toLowerCase();

  // Dumb scrapers that claim to be Chrome but don't have window.chrome
  if (low.includes("chrome") && typeof (window as any).chrome === "undefined") return true;

  // No languages array is suspicious (real browsers always have at least one)
  if (!navigator.languages || navigator.languages.length === 0) return true;

  // Obvious automation UA fragments that slip past other filters
  const botFragments = ["headless", "phantomjs", "puppeteer", "playwright", "selenium", "webdriver"];
  if (botFragments.some((f) => low.includes(f))) return true;

  return false;
}

export function track(data: TrackEvent) {
  try {
    // Skip known bot signals on the client. Server /api/track has its own filter;
    // this just saves a round trip and cuts noise before it leaves the browser.
    if (looksLikeBot() || isOwnerBrowser()) return;

    const deviceId = getDeviceId();

    // Use sendBeacon for reliability (survives page navigation)
    const payload = JSON.stringify({
      ...data,
      meta: coarsenMeta(data.meta),
      url: typeof window !== "undefined" ? window.location.pathname : "",
      timestamp: new Date().toISOString(),
      deviceId,
    });

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon("/api/track", payload);
    } else {
      // Fallback for older browsers
      fetch("/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never throw — tracking should be invisible to the user
  }
}

/** Expose device ID so the admin page can show it for exclusion. */
export function getCurrentDeviceId(): string {
  return getDeviceId();
}
